import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react';
import { articles } from '../mock';

const Article = () => {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="not-found">
        <h1>Article non trouvé</h1>
        <Link to="/guides" className="back-link">Retour aux guides</Link>
      </div>
    );
  }

  return (
    <main className="article-container">
      <Link to="/guides" className="back-button">
        <ArrowLeft size={20} />
        Retour aux guides
      </Link>

      <article className="article-content">
        {/* Article Header */}
        <header className="article-header">
          <span className="article-category">{article.categorie}</span>
          <h1 className="article-title">{article.titre}</h1>
          
          <div className="article-meta">
            <span className="meta-item">
              <User size={18} />
              {article.auteur}
            </span>
            <span className="meta-item">
              <Calendar size={18} />
              {new Date(article.date).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className="meta-item">
              <Clock size={18} />
              {article.tempsDeLecture} de lecture
            </span>
          </div>
        </header>

        {/* Featured Image */}
        <div className="featured-image-container">
          <img src={article.image} alt={article.titre} className="featured-image" />
        </div>

        {/* Article Body */}
        <div 
          className="article-body"
          dangerouslySetInnerHTML={{ __html: article.contenu }}
        />
      </article>

      {/* Related Articles */}
      <section className="related-section">
        <h2 className="related-title">Autres articles</h2>
        <div className="related-grid">
          {articles
            .filter((a) => a.id !== article.id)
            .slice(0, 3)
            .map((relatedArticle) => (
              <Link
                key={relatedArticle.id}
                to={`/article/${relatedArticle.slug}`}
                className="related-card card"
              >
                <div className="related-image-wrapper">
                  <img src={relatedArticle.image} alt={relatedArticle.titre} className="related-image" />
                </div>
                <h3 className="related-card-title">{relatedArticle.titre}</h3>
                <p className="related-card-category">{relatedArticle.categorie}</p>
              </Link>
            ))}
        </div>
      </section>

      <style jsx>{`
        .article-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 2rem;
          min-height: 80vh;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #2d5a8c;
          text-decoration: none;
          font-weight: 600;
          margin-bottom: 2.5rem;
          padding: 0.75rem 0;
          transition: gap 0.2s ease;
        }

        .back-button:hover {
          gap: 0.75rem;
          transform: none;
        }

        .article-content {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 4rem;
        }

        .article-header {
          padding: 3rem 3rem 2rem;
          text-align: center;
        }

        .article-category {
          display: inline-block;
          background: rgba(45, 90, 140, 0.1);
          color: #2d5a8c;
          padding: 0.5rem 1.25rem;
          border-radius: 25px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
        }

        .article-title {
          font-size: 2.75rem;
          color: #1a365d;
          margin-bottom: 1.5rem;
          font-weight: 700;
          line-height: 1.2;
        }

        .article-meta {
          display: flex;
          justify-content: center;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #718096;
          font-size: 0.95rem;
        }

        .featured-image-container {
          width: 100%;
          position: relative;
          padding-top: 50%;
          overflow: hidden;
          background: #f7fafc;
        }

        .featured-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .article-body {
          padding: 3rem;
          line-height: 1.8;
          color: #2d3748;
          font-size: 1.05rem;
        }

        .article-body h2 {
          font-size: 2rem;
          color: #1a365d;
          margin-top: 2.5rem;
          margin-bottom: 1.25rem;
          font-weight: 600;
        }

        .article-body h3 {
          font-size: 1.5rem;
          color: #2d5a8c;
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .article-body p {
          margin-bottom: 1.5rem;
        }

        .article-body ul,
        .article-body ol {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }

        .article-body li {
          margin-bottom: 0.75rem;
        }

        .article-body strong {
          color: #1a365d;
          font-weight: 600;
        }

        .article-body table {
          width: 100%;
          border-collapse: collapse;
          margin: 2rem 0;
        }

        .article-body th,
        .article-body td {
          padding: 1rem;
          text-align: left;
          border: 1px solid #e2e8f0;
        }

        .article-body th {
          background: rgba(45, 90, 140, 0.1);
          color: #1a365d;
          font-weight: 600;
        }

        .related-section {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem 0;
        }

        .related-title {
          font-size: 2rem;
          color: #1a365d;
          margin-bottom: 2rem;
          font-weight: 600;
          text-align: center;
        }

        .related-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .related-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          text-decoration: none;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        }

        .related-image-wrapper {
          position: relative;
          padding-top: 60%;
          overflow: hidden;
          background: #f7fafc;
        }

        .related-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .related-card:hover .related-image {
          transform: scale(1.05);
        }

        .related-card-title {
          padding: 1.25rem 1.25rem 0.75rem;
          font-size: 1.15rem;
          color: #1a365d;
          font-weight: 600;
          line-height: 1.4;
        }

        .related-card-category {
          padding: 0 1.25rem 1.25rem;
          color: #718096;
          font-size: 0.9rem;
        }

        .not-found {
          max-width: 600px;
          margin: 5rem auto;
          text-align: center;
          padding: 2rem;
        }

        .not-found h1 {
          color: #1a365d;
          margin-bottom: 1.5rem;
        }

        .back-link {
          color: #2d5a8c;
          text-decoration: none;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .article-container {
            padding: 1.5rem;
          }

          .article-header {
            padding: 2rem 1.5rem 1.5rem;
          }

          .article-title {
            font-size: 1.85rem;
          }

          .article-meta {
            flex-direction: column;
            gap: 0.75rem;
          }

          .article-body {
            padding: 2rem 1.5rem;
            font-size: 1rem;
          }

          .article-body h2 {
            font-size: 1.5rem;
          }

          .article-body h3 {
            font-size: 1.25rem;
          }

          .related-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </main>
  );
};

export default Article;